
import React, { useRef, useEffect, useState } from 'react';

interface BabyVisualizerProps {
  week: number;
}

export const BabyVisualizer: React.FC<BabyVisualizerProps> = ({ week }) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const [isVisible, setIsVisible] = useState(false);
  const [isPageVisible, setIsPageVisible] = useState(!document.hidden);

  // Track page visibility to completely pause animation when tab is hidden
  useEffect(() => {
    const handleVisibilityChange = () => {
      setIsPageVisible(!document.hidden);
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);
    return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
  }, []);

  // Intersection Observer to only animate when visible
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const observer = new IntersectionObserver(
      (entries) => {
        setIsVisible(entries[0].isIntersecting);
      },
      { threshold: 0.1 }
    );

    observer.observe(canvas);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const ctx = canvas.getContext('2d');
    if (!ctx) return;

    // Don't start animation loop if page is hidden
    if (!isPageVisible) return;

    // Set dimensions
    const resize = () => {
      const parent = canvas.parentElement;
      if (parent) {
        canvas.width = parent.clientWidth;
        canvas.height = parent.clientHeight;
      }
    };
    window.addEventListener('resize', resize);
    resize();

    let animationFrameId: number;
    let time = 0;
    let lastFrameTime = 0;
    const targetFPS = 24; // Reduce from 60fps to 24fps - still smooth, much less CPU
    const frameInterval = 1000 / targetFPS;

    // Config based on trimester
    const trimester = week < 14 ? 1 : week < 28 ? 2 : 3;

    // Color palettes (R, G, B)
    const colors = trimester === 1
      ? { r: 52, g: 211, b: 153 } // Emerald/Cyan (Cellular)
      : trimester === 2
        ? { r: 251, g: 191, b: 36 } // Amber/Orange (Warmth)
        : { r: 244, g: 63, b: 94 }; // Rose/Purple (Deep)

    // Heartrate simulation (faster early on, slightly slower later)
    const pulseSpeed = trimester === 1 ? 0.08 : trimester === 2 ? 0.06 : 0.05;

    // Particles - cap at 30 max for performance (was week * 3 = up to 120)
    const particleCount = Math.min(week, 30);
    const particles = Array.from({ length: particleCount }, () => ({
      x: Math.random() * canvas.width,
      y: Math.random() * canvas.height,
      size: Math.random() * 2 + 1,
      speedX: (Math.random() - 0.5) * 0.5,
      speedY: (Math.random() - 0.5) * 0.5,
      alpha: Math.random() * 0.5
    }));

    // Pre-calculate static values
    const centerX = canvas.width / 2;
    const centerY = canvas.height / 2;
    const baseRadius = Math.min(canvas.width, canvas.height) * (0.15 + (week / 40) * 0.25);

    const render = (currentTime: number) => {
      // Completely stop if page is hidden - don't schedule next frame
      if (document.hidden) {
        return;
      }

      // Only animate if visible in viewport
      if (!isVisible) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }

      // Throttle to target FPS
      const elapsed = currentTime - lastFrameTime;
      if (elapsed < frameInterval) {
        animationFrameId = requestAnimationFrame(render);
        return;
      }
      lastFrameTime = currentTime - (elapsed % frameInterval);

      time += pulseSpeed;
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      // Pulse calculation
      const pulse = Math.sin(time) * 0.05 + 1; // 0.95 to 1.05
      const currentRadius = baseRadius * pulse;

      // 1. Draw Outer Glow (Atmosphere)
      const glow = ctx.createRadialGradient(centerX, centerY, currentRadius * 0.5, centerX, centerY, currentRadius * 2);
      glow.addColorStop(0, `rgba(${colors.r}, ${colors.g}, ${colors.b}, 0.2)`);
      glow.addColorStop(1, `rgba(${colors.r}, ${colors.g}, ${colors.b}, 0)`);
      ctx.fillStyle = glow;
      ctx.fillRect(0, 0, canvas.width, canvas.height);

      // 2. Draw Core Orb
      ctx.beginPath();
      ctx.arc(centerX, centerY, currentRadius, 0, Math.PI * 2);
      const coreGradient = ctx.createRadialGradient(centerX - currentRadius * 0.3, centerY - currentRadius * 0.3, 0, centerX, centerY, currentRadius);
      coreGradient.addColorStop(0, `rgba(255, 255, 255, 0.8)`);
      coreGradient.addColorStop(0.4, `rgba(${colors.r}, ${colors.g}, ${colors.b}, 0.6)`);
      coreGradient.addColorStop(1, `rgba(${colors.r}, ${colors.g}, ${colors.b}, 0.1)`);
      ctx.fillStyle = coreGradient;
      ctx.fill();

      // 3. Particles (simplified - skip connection lines for performance)
      particles.forEach(p => {
        p.x += p.speedX;
        p.y += p.speedY;

        // Wrap around
        if (p.x < 0) p.x = canvas.width;
        if (p.x > canvas.width) p.x = 0;
        if (p.y < 0) p.y = canvas.height;
        if (p.y > canvas.height) p.y = 0;

        // Draw particle
        ctx.beginPath();
        ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(255, 255, 255, ${p.alpha})`;
        ctx.fill();
      });

      animationFrameId = requestAnimationFrame(render);
    };

    animationFrameId = requestAnimationFrame(render);

    return () => {
      window.removeEventListener('resize', resize);
      cancelAnimationFrame(animationFrameId);
    };
  }, [week, isVisible, isPageVisible]);

  return <canvas ref={canvasRef} className="w-full h-full absolute inset-0" />;
};

